import React, { useState } from "react";
import { toast } from 'react-toastify';
import ShareImageViewer from "./ShareImageViewer";
import ShareThumb from "./ShareThumb";
import { getPreviewCandidates, getViewerTileSource } from "./shareImage";
import { useImageCloudfront, formatShareDate, getShareStatus, toPlainText, getShareLink, getTimeLeft } from "./useShareDetails";

// read-only details of one shared link - used by both the view and edit pages.
// `children` renders full width above the details (edit puts the extend panel there).
const ShareDetailsContent = ({ shareData, shareid, now, hideCountdown, onExtend, canExtend, children }) => {
    const imageCloudfront = useImageCloudfront();
    const [viewer, setViewer] = useState(null); // { tileSource } while the zoom viewer is open

    const shareLink = getShareLink(shareData?.shareid || shareid);
    const isExpired = !!shareData?.isExpired;
    const timeLeft = getTimeLeft(shareData, now);
    const diagnosis = toPlainText(shareData?.diagnosis);
    const hasImage = getPreviewCandidates(shareData, imageCloudfront).length > 0;
    const noImage = (
        <div className="sv-image sv-image-empty">
            <i className="mdi mdi-image-off-outline"></i>
            <span>Preview not available</span>
        </div>
    );

    const handleCopyShareLink = () => {
        navigator.clipboard.writeText(shareLink).then(() => {
            toast.success("Link copied to clipboard!", { position: "bottom-center", autoClose: 2000, theme: "light" });
        }).catch(() => {
            toast.error("Failed to copy link.", { position: "bottom-center", autoClose: 2000, theme: "light" });
        });
    };

    const handleWhatsAppShare = () => {
        const text = encodeURIComponent(`View this histology image: ${shareLink}`);
        window.open(`https://wa.me/?text=${text}`, '_blank');
    };

    return (
        <div className="sv">
            <div className="row g-4">
                {children && <div className="col-12">{children}</div>}

                {/* Image preview */}
                <div className="col-lg-5">
                    <div className="sv-card sv-image-card">
                        {hasImage ? (
                            <div className="sv-image" onClick={() => setViewer({ tileSource: getViewerTileSource(shareData, imageCloudfront) })} title="Open image viewer">
                                <ShareThumb row={shareData} imageCloudfront={imageCloudfront} width={800} fallback={noImage} />
                                <span className="sv-image-hint">
                                    <i className="mdi mdi-magnify-plus-outline"></i>
                                    Click to zoom
                                </span>
                            </div>
                        ) : noImage}
                    </div>
                </div>

                {/* Summary + details */}
                <div className="col-lg-7">
                    <div className="sv-card h-100">
                        <div className="d-flex flex-wrap align-items-center gap-2">
                            {shareData?.caseid ? (
                                <a
                                    href={`/viewcase/${shareData.caseid}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="sv-case-badge"
                                    title="Open case in new tab"
                                >
                                    {shareData?.diagnosisid || "View case"}
                                    <span className="sv-case-icon"><i className="mdi mdi-arrow-top-right"></i></span>
                                </a>
                            ) : (
                                <span className="sv-case-badge">{shareData?.diagnosisid || "—"}</span>
                            )}
                            <span className={`sv-status ${isExpired ? "sv-status-expired" : "sv-status-active"}`}>
                                <span className="sv-status-dot"></span>
                                {getShareStatus(shareData)}
                            </span>
                        </div>
                        <div className="sv-label mt-3">Diagnosis</div>
                        <div className={`sv-diagnosis ${diagnosis ? "" : "sv-muted"}`}>{diagnosis || "No diagnosis added"}</div>

                        {!hideCountdown && (
                            <div className={`sv-countdown sv-countdown-${timeLeft.tone}`}>
                                <i className={`mdi ${isExpired ? "mdi-timer-off-outline" : "mdi-timer-sand"}`}></i>
                                <div>
                                    <div className="sv-countdown-label">{isExpired ? "Link expired" : "Link expires in"}</div>
                                    <div className="sv-countdown-value">{isExpired ? timeLeft.label : timeLeft.label.replace(/ left$/, "")}</div>
                                </div>
                                <div className="sv-countdown-date">
                                    <div className="sv-countdown-label">Expires at</div>
                                    <div>{formatShareDate(shareData?.expiresAt) || "—"}</div>
                                </div>
                            </div>
                        )}

                        <div className={`sv-details ${hideCountdown ? "mt-4" : ""}`}>
                            {/* without the countdown box, expires at moves into the grid; otherwise created by takes the full row */}
                            <div className={hideCountdown ? "" : "sv-detail-wide"}>
                                <div className="sv-label">Created by</div>
                                <div className="sv-value">{shareData?.createdByName || "—"}</div>
                                {shareData?.createdByEmail && <div className="sv-sub">{shareData.createdByEmail}</div>}
                            </div>
                            {hideCountdown && (
                                <div>
                                    <div className="sv-label">Expires at</div>
                                    <div className="sv-value">{formatShareDate(shareData?.expiresAt) || "—"}</div>
                                </div>
                            )}
                            <div>
                                <div className="sv-label">Created on</div>
                                <div className="sv-value">{formatShareDate(shareData?.created) || "—"}</div>
                            </div>
                            <div>
                                <div className="sv-label">Updated on</div>
                                <div className="sv-value">{formatShareDate(shareData?.updated) || "—"}</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Share link */}
                <div className="col-12">
                    <div className="sv-card">
                        <div className="d-flex align-items-center gap-2 mb-3">
                            <span className="sv-share-icon"><i className="mdi mdi-link-variant"></i></span>
                            <div>
                                <div className="sv-share-title">Share link</div>
                                <div className="sv-sub">
                                    {isExpired
                                        ? "This link has expired — anyone opening it will see a \"Link Expired\" page."
                                        : "Anyone with this link can view the image until it expires."}
                                </div>
                            </div>
                        </div>

                        <div className={`sv-link-box ${isExpired ? "sv-link-box-expired" : ""}`}>
                            <i className="mdi mdi-web"></i>
                            <span className="sv-link-text" title={shareLink}>{shareLink}</span>
                        </div>

                        <div className="d-flex flex-wrap gap-2 mt-3">
                            {isExpired ? (
                                canExtend && onExtend && (
                                    <button type="button" className="sv-btn sv-btn-orange" onClick={onExtend}>
                                        <span className="material-symbols-outlined">update</span>
                                        Extend Share Link
                                    </button>
                                )
                            ) : (
                                <>
                                    <button type="button" className="sv-btn sv-btn-dark" onClick={handleCopyShareLink}>
                                        <span className="material-symbols-outlined">content_copy</span>
                                        Copy Link
                                    </button>
                                    <a className="sv-btn sv-btn-light" href={shareLink} target="_blank" rel="noopener noreferrer">
                                        <span className="material-symbols-outlined">open_in_new</span>
                                        Open Link
                                    </a>
                                    <button type="button" className="sv-btn sv-btn-whatsapp" onClick={handleWhatsAppShare}>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
                                        WhatsApp
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {viewer && (
                <ShareImageViewer
                    tileSource={viewer.tileSource}
                    title={shareData?.diagnosisid}
                    onClose={() => setViewer(null)}
                />
            )}

            <style>
                {`
                    .sv { --sv-orange: #F2A341; --sv-dark: #1E222B; --sv-border: #e5e7eb; --sv-muted: #6b7280; }
                    .sv-card {
                        background: #fff;
                        border: 1px solid var(--sv-border);
                        border-radius: 12px;
                        padding: 20px;
                    }
                    .sv-image-card { padding: 12px; height: 100%; }
                    .sv-image {
                        position: relative;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        height: 100%;
                        min-height: 320px;
                        max-height: 460px;
                        background: #111316;
                        border-radius: 8px;
                        overflow: hidden;
                        cursor: zoom-in;
                    }
                    .sv-image img {
                        max-width: 100%;
                        max-height: 460px;
                        object-fit: contain;
                        display: block;
                        transition: transform 0.3s ease;
                    }
                    .sv-image:hover img { transform: scale(1.03); }
                    .sv-image-hint {
                        position: absolute;
                        bottom: 12px;
                        right: 12px;
                        display: inline-flex;
                        align-items: center;
                        gap: 6px;
                        padding: 6px 12px;
                        border-radius: 999px;
                        background: rgba(0, 0, 0, 0.6);
                        color: #fff;
                        font-size: 12px;
                        font-weight: 600;
                        pointer-events: none;
                    }
                    .sv-image-hint .mdi { font-size: 16px; line-height: 1; }
                    .sv-image-empty { cursor: default; flex-direction: column; gap: 6px; color: #9ca3af; background: #f3f4f6; }
                    .sv-image-empty .mdi { font-size: 40px; }

                    .sv-case-badge {
                        display: inline-flex;
                        align-items: center;
                        gap: 8px;
                        padding: 4px 4px 4px 14px;
                        border-radius: 999px;
                        background: #fff;
                        border: 1px solid var(--sv-orange);
                        color: var(--sv-dark);
                        font-size: 14px;
                        font-weight: 700;
                        text-decoration: none;
                        box-shadow: 0 1px 2px rgba(242, 163, 65, 0.25);
                        transition: background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
                    }
                    .sv-case-icon {
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        width: 24px;
                        height: 24px;
                        border-radius: 50%;
                        background: var(--sv-orange);
                        color: #fff;
                        font-size: 14px;
                        transition: background-color 0.2s ease, color 0.2s ease;
                    }
                    a.sv-case-badge:hover {
                        background: var(--sv-orange);
                        color: #fff;
                        box-shadow: 0 3px 8px rgba(242, 163, 65, 0.4);
                        transform: translateY(-1px);
                    }
                    a.sv-case-badge:hover .sv-case-icon { background: #fff; color: var(--sv-orange); }

                    .sv-status {
                        display: inline-flex;
                        align-items: center;
                        gap: 6px;
                        padding: 5px 12px;
                        border-radius: 999px;
                        font-size: 12px;
                        font-weight: 600;
                    }
                    .sv-status-dot { width: 7px; height: 7px; border-radius: 50%; background: currentColor; }
                    .sv-status-active { background: #def7ec; color: #046c4e; border: 1px solid #84e1bc; }
                    .sv-status-expired { background: #fde8e8; color: #c81e1e; border: 1px solid #f8b4b4; }

                    .sv-label {
                        font-size: 11px;
                        font-weight: 600;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                        color: var(--sv-muted);
                        margin-bottom: 4px;
                    }
                    .sv-diagnosis { font-size: 18px; font-weight: 600; color: var(--sv-dark); line-height: 1.4; word-break: break-word; }
                    .sv-muted { color: #9ca3af; font-weight: 500; font-size: 15px; }
                    .sv-value { font-size: 14px; font-weight: 600; color: var(--sv-dark); }
                    .sv-sub { font-size: 12px; color: var(--sv-muted); }

                    .sv-countdown {
                        display: flex;
                        align-items: center;
                        gap: 12px;
                        margin: 20px 0;
                        padding: 14px 16px;
                        border-radius: 10px;
                        border: 1px solid;
                    }
                    .sv-countdown > .mdi { font-size: 26px; line-height: 1; }
                    .sv-countdown-label { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; opacity: 0.8; }
                    .sv-countdown-value { font-size: 20px; font-weight: 700; line-height: 1.2; }
                    .sv-countdown-date { margin-left: auto; text-align: right; font-size: 13px; font-weight: 600; }
                    .sv-countdown-ok { background: #ecfdf5; border-color: #a7f3d0; color: #046c4e; }
                    .sv-countdown-soon { background: #fff8ee; border-color: #F2A341; color: #b45309; }
                    .sv-countdown-urgent { background: #fef2f2; border-color: #fca5a5; color: #c81e1e; }
                    .sv-countdown-expired, .sv-countdown-none { background: #f9fafb; border-color: var(--sv-border); color: var(--sv-muted); }

                    .sv-details {
                        display: grid;
                        grid-template-columns: repeat(2, minmax(0, 1fr));
                        gap: 18px 24px;
                        padding-top: 18px;
                        border-top: 1px dashed var(--sv-border);
                    }
                    .sv-detail-wide { grid-column: 1 / -1; }
                    @media (max-width: 575px) {
                        .sv-details { grid-template-columns: 1fr; }
                        .sv-countdown { flex-wrap: wrap; }
                        .sv-countdown-date { margin-left: 0; text-align: left; width: 100%; }
                    }

                    .sv-share-icon {
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        width: 38px;
                        height: 38px;
                        border-radius: 10px;
                        background: #fff3e0;
                        color: var(--sv-orange);
                        font-size: 22px;
                        flex-shrink: 0;
                    }
                    .sv-share-title { font-size: 15px; font-weight: 700; color: var(--sv-dark); }
                    .sv-link-box {
                        display: flex;
                        align-items: center;
                        gap: 10px;
                        padding: 14px 16px;
                        border-radius: 10px;
                        background: #f8f9fa;
                        border: 1px dashed #cbd5e1;
                        color: var(--sv-dark);
                    }
                    .sv-link-box .mdi { font-size: 20px; color: var(--sv-orange); flex-shrink: 0; }
                    .sv-link-text {
                        font-family: SFMono-Regular, Menlo, Consolas, monospace;
                        font-size: 14px;
                        word-break: break-all;
                        user-select: all;
                    }
                    .sv-link-box-expired { opacity: 0.6; }
                    .sv-link-box-expired .sv-link-text { text-decoration: line-through; }

                    .sv-btn {
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        gap: 6px;
                        padding: 9px 18px;
                        border-radius: 8px;
                        border: 1px solid transparent;
                        font-size: 14px;
                        font-weight: 600;
                        text-decoration: none;
                        cursor: pointer;
                        transition: filter 0.2s ease, background-color 0.2s ease;
                    }
                    .sv-btn .material-symbols-outlined { font-size: 18px; }
                    .sv-btn:hover { filter: brightness(1.1); }
                    .sv-btn:disabled { opacity: 0.45; cursor: not-allowed; filter: none; }
                    .sv-btn-dark { background: var(--sv-dark); color: #fff; }
                    .sv-btn-dark:hover { color: #fff; }
                    .sv-btn-light { background: #fff; color: var(--sv-dark); border-color: var(--sv-border); }
                    .sv-btn-light:hover { background: #f9fafb; color: var(--sv-dark); filter: none; }
                    .sv-btn-whatsapp { background: #25D366; color: #fff; }
                    .sv-btn-orange { background: var(--sv-orange); color: #fff; }
                    .sv-btn-orange:hover { color: #fff; }
                `}
            </style>
        </div>
    );
};

export default ShareDetailsContent;
